import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dropdown, Badge, Button, Offcanvas, ListGroup } from 'react-bootstrap';
import { useNotifications } from '../contexts/NotificationsContext';
import { getNotificationIcon, getNotificationIconColor, getNotificationIconBg } from '../utils/notificationIcons';
import { getNotificationTargetPath } from '../utils/notificationNavigation';
import { Bell } from 'lucide-react';

const NotificationsDropdown = () => {
  const { notifications, dismissNotification, clearAllNotifications, getNotificationCount } = useNotifications();
  const navigate = useNavigate();
  const [expandedNotifications, setExpandedNotifications] = useState(new Set());
  const [showRecurringModal, setShowRecurringModal] = useState(false);
  const [selectedRecurringEvaluation, setSelectedRecurringEvaluation] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showAllNotificationsOffcanvas, setShowAllNotificationsOffcanvas] = useState(false);

  // Get user role from localStorage
  const userRole = localStorage.getItem('role');

  // Add CSS to remove dropdown arrow and make mobile full screen
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .no-arrow::after {
        display: none !important;
      }
      
      @media (max-width: 768px) {
        .notification-dropdown {
          width: 100vw !important;
          height: 100vh !important;
          max-width: 100vw !important;
          max-height: 100vh !important;
          position: fixed !important;
          top: 0 !important;
          left: 0 !important;
          margin: 0 !important;
          border-radius: 0 !important;
          border: none !important;
          z-index: 9999 !important;
        }
        
        .notification-dropdown .dropdown-header {
          border-radius: 0 !important;
          padding: 1rem !important;
          background-color: var(--bs-body-bg) !important;
          border-bottom: 1px solid var(--bs-border-color) !important;
        }
        
        .notification-dropdown .dropdown-item {
          padding: 1rem !important;
        }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  // Complete evaluation now
  const completeEvaluationNow = (evaluation) => {
    navigate(`/evaluations/${evaluation.id}/complete`);
  };

  // Handle work log notification
  const handleWorkLogNotification = (data) => {
    if (!data) {
      console.error('Invalid work log notification data:', data);
      return;
    }
    
    // Backend notifications store residentId directly, frontend notifications store it as data.resident.id
    const residentId = data.residentId || (data.resident && data.resident.id);
    const month = data.month;
    const year = data.year;
    
    if (!residentId || month === undefined || year === undefined) {
      console.error('Missing required data for work log navigation:', { residentId, month, year });
      return;
    }
    if (userRole === 'resident') {
      navigate(`/resident/work-hours#tab=work-hours`);
    } else {
      navigate(`/director/work-log/${residentId}/${month}/${year}`);
    }
  };

  // Handle leave request notification
  const handleLeaveRequestNotification = (data) => {
    navigate('/director/work-logs?tab=vacation');
  };

  // Handle evaluation assignment notification
  const handleEvaluationAssignmentNotification = (data) => {
    // Check if this is a document assignment (onboarding) based on the data structure
    if (data.itemType === 'document' || data.itemType === 'package' || data.itemType === 'request') {
      // This is a document assignment, navigate to onboarding
      if (userRole === 'resident') {
        navigate('/onboarding#tab=my');
      } else {
        navigate('/onboarding#tab=manage');
      }
      return;
    }
    
    // Check if this is for a resident (survey/questionnaire) or attending (performance/feedback)
    if (data.evaluationType === 'survey' || data.evaluationType === 'questionnaire') {
      // For residents, navigate directly to the evaluation form
      navigate(`/resident/evaluation/fill/${data.templateId}`);
    } else {
      // For attendings, navigate to their assigned evaluations
      navigate('/attending/assigned');
    }
  };

  // Handle notification click navigation
  const handleNotificationClick = (notification) => {
    const explicitTarget = getNotificationTargetPath(notification, userRole);
    if (explicitTarget) {
      navigate(explicitTarget);
      setShowDropdown(false);
      setShowAllNotificationsOffcanvas(false);
      return;
    }

    switch (notification.type) {
      case 'evaluation':
        completeEvaluationNow(notification.data);
        break;
      case 'worklog':
        handleWorkLogNotification(notification.data);
        break;
      case 'leaveRequest':
        handleLeaveRequestNotification(notification.data);
        break;
      case 'schedule':
        navigate('/schedules');
        break;
      case 'announcement':
        // Could navigate to announcements page or show modal
        break;
      case 'reminder':
        // Check if this is an onboarding reminder based on the data
        if (notification.data && (notification.data.itemType === 'package' || notification.data.itemType === 'document' || notification.data.itemType === 'request')) {
          // This is an onboarding reminder
          if (userRole === 'resident') {
            navigate('/onboarding#tab=my');
          } else {
            navigate('/onboarding#tab=manage');
          }
        } else {
          // This is a regular reminder (evaluation, etc.)
          navigate('/attending/assigned');
        }
        break;
      case 'approval':
        navigate('/resident/work-hours#tab=vacation');
        break;
      case 'rejection':
        navigate('/resident/work-hours#tab=vacation');
        break;
      case 'assignment':
        handleEvaluationAssignmentNotification(notification.data);
        break;
      case 'completion':
        navigate('/director/evaluations?tab=completed');
        break;
      case 'overdue':
        navigate('/attending/assigned');
        break;
      case 'eventReminder':
        navigate('/calendar');
        break;
      case 'system':
        // Handle system notification - just dismiss
        dismissNotification(notification.id);
        break;
      case 'inServiceExam':
        navigate('/director/in-service-exams');
        break;
      case 'eventInvitation':
        navigate('/calendar');
        break;
      case 'eventInvitationResponse':
        navigate('/calendar');
        break;
      case 'unlockRequest':
        navigate('/director/work-logs');
        break;
      case 'profile':
        navigate('/profile');
        break;
      case 'onboardingAssignment':
        // Navigate to onboarding page for residents, manage tab for directors
        if (userRole === 'resident') {
          navigate('/onboarding#tab=my');
        } else {
          navigate('/onboarding#tab=manage');
        }
        break;
      case 'onboardingSubmission':
        // Navigate to submissions tab for directors
        if (userRole === 'director') {
          navigate('/onboarding#tab=submissions');
        } else {
          navigate('/onboarding#tab=my');
        }
        break;
      case 'onboardingApproval':
      case 'onboardingRejection':
        // Navigate to completed tab for residents, submissions for directors
        if (userRole === 'resident') {
          navigate('/onboarding#tab=completed');
        } else {
          navigate('/onboarding#tab=submissions');
        }
        break;
      case 'onboardingOverdue':
        // Navigate to my tab for residents to see overdue items
        if (userRole === 'resident') {
          navigate('/onboarding#tab=my');
        } else {
          navigate('/onboarding#tab=manage');
        }
        break;
      default:
        break;
    }
    // Close dropdown after navigation
    setShowDropdown(false);
    setShowAllNotificationsOffcanvas(false);
  };

  // Handle recurring evaluation selection
  const handleRecurringEvaluationClick = (evaluation) => {
    setSelectedRecurringEvaluation(evaluation);
    setShowRecurringModal(true);
  };

  // Format time ago
  const formatTimeAgo = (date) => {
    const now = new Date();
    const diffInMinutes = Math.floor((now - new Date(date)) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ago`;
  };

  const notificationCount = getNotificationCount();

  // Component to handle text truncation and expansion
  const TruncatedText = ({ text, notificationId, maxLength = 100 }) => {
    const isExpanded = expandedNotifications.has(notificationId);
    const shouldTruncate = text.length > maxLength;

    const toggleExpanded = (e) => {
      e.stopPropagation();
      e.preventDefault();
      setExpandedNotifications(prev => {
        const newSet = new Set(prev);
        if (isExpanded) {
          newSet.delete(notificationId);
        } else {
          newSet.add(notificationId);
        }
        return newSet;
      });
    };

    if (!shouldTruncate) {
      return <p className="mb-1 small text-muted text-wrap">{text}</p>;
    }

    return (
      <div className="mb-1">
        <p className="small text-muted text-wrap mb-1">
          {isExpanded ? text : `${text.substring(0, maxLength)}...`}
        </p>
        <Button
          variant="link"
          size="sm"
          className="p-0 text-decoration-none small"
          onClick={toggleExpanded}
        >
          {isExpanded ? 'Show less' : 'Show more'}
        </Button>
      </div>
    );
  };

  return (
    <>
      <Dropdown align="end" show={showDropdown} onToggle={(isOpen) => setShowDropdown(isOpen)}>
        <Dropdown.Toggle variant="light-lite" size="sm" className="position-relative no-arrow">
          <Bell fill="var(--white)"/>
          {notificationCount > 0 && (
            <Badge 
              bg="primary" 
              className="position-absolute top-0 start-0 translate-middle p-1 px-2 rounded-pill"
              style={{ fontSize: '0.6rem', minWidth: '18px' }}
            >
              {notificationCount > 99 ? '99+' : notificationCount}
            </Badge>
          )}
        </Dropdown.Toggle>

        <Dropdown.Menu 
          style={{ 
            width: '450px', 
            maxHeight: '660px', 
            overflowY: 'auto'
          }} 
          className="shadow-lg notification-dropdown rounded-3"
        >
          <Dropdown.Header className="d-flex justify-content-between align-items-center">
            <span>Notifications</span>
            <div className="d-flex align-items-center gap-2">
              {notificationCount > 0 && (
                <Button 
                  variant="link" 
                  size="sm" 
                  className="p-0 text-decoration-none"
                  onClick={(e) => {
                    e.stopPropagation();
                    clearAllNotifications();
                  }}
                >
                  Clear All
                </Button>
              )}
              <Button 
                variant="light-lite" 
                size="sm" 
                className="px-2 py-1 ms-3 text-decoration-none d-md-none"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDropdown(false);
                }}
              >
                <i className="bi bi-x-lg"></i>
              </Button>
            </div>
          </Dropdown.Header>
          
          {notifications.length === 0 ? (
            <Dropdown.Item disabled className="text-center text-muted py-3">
              <i className="bi bi-bell mb-2" style={{ fontSize: '24px' }}></i>
              <br />
              No notifications
            </Dropdown.Item>
          ) : (
            <>
              {notifications.slice(0, 5).map((notification) => (
                (() => {
                  const NotificationIcon = getNotificationIcon(notification.type);
                  return (
                <Dropdown.Item 
                  key={notification.id} 
                  className="px-3 py-2 border-solid-bottom"
                  onClick={() => handleNotificationClick(notification)}
                  style={{ cursor: 'pointer' }}
                >
                  <div>
                    <div className="d-flex align-items-center gap-3">
                      {/* Notification Icon */}
                      <div className={`rounded-circle d-flex align-items-center justify-content-center ${getNotificationIconBg(notification.type)}`} 
                           style={{ width: '40px', height: '40px', minWidth: '40px' }}>
                        <NotificationIcon className={getNotificationIconColor(notification.type)} size={18} />
                      </div>
                      
                      {/* Notification Content */}
                      <div className="flex-grow-1">
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="d-flex flex-row align-items-center gap-2 "><h6 className="m-0 small text-wrap">{notification.title}</h6>
                          <small className="text-muted">
                            {formatTimeAgo(notification.createdAt)}
                          </small> </div>
                          <Button 
                            variant="link-dng"
                            size="sm"
                            className="py-0 px-1"
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              dismissNotification(notification.id);
                            }}
                          >
                            <i className="bi bi-x"></i>
                          </Button>
                        </div>
                        <TruncatedText text={notification.body} notificationId={notification.id} />
                      </div>
                    </div>
                  </div>
                </Dropdown.Item>
                  );
                })()
              ))}
              
              {notifications.length > 5 && (
                <Dropdown.Item 
                  className="text-center py-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDropdown(false);
                    setShowAllNotificationsOffcanvas(true);
                  }}
                >
                  View all {notifications.length} notifications
                </Dropdown.Item>
              )}
            </>
          )}
        </Dropdown.Menu>
      </Dropdown>

      {/* All Notifications Offcanvas */}
      <Offcanvas 
        show={showAllNotificationsOffcanvas} 
        onHide={() => setShowAllNotificationsOffcanvas(false)}
        placement="end"
        style={{ width: '450px' }}
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title className="d-flex flex-row align-items-center justify-content-center">
            <span className="fw-bold">All Notifications</span>
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="p-0">
          <ListGroup variant="flush">
            {notifications.length === 0 ? (
              <ListGroup.Item className="text-center text-muted py-5">
                <i className="bi bi-bell" style={{ fontSize: '48px' }}></i>
                <br />
                <h6>No notifications</h6>
                <p className="text-muted">You're all caught up!</p>
              </ListGroup.Item>
            ) : (
              notifications.map((notification) => (
                (() => {
                  const NotificationIcon = getNotificationIcon(notification.type);
                  return (
                <ListGroup.Item 
                  key={notification.id} 
                  className="border-bottom"
                  onClick={() => handleNotificationClick(notification)}

                >
                  <div className="d-flex align-items-start gap-3">
                    {/* Notification Icon */}
                    <div className={`rounded-circle d-flex align-items-center justify-content-center ${getNotificationIconBg(notification.type)}`} 
                         style={{ width: '40px', height: '40px', minWidth: '40px' }}>
                      <NotificationIcon className={getNotificationIconColor(notification.type)} size={20} />
                    </div>
                    
                    {/* Notification Content */}
                    <div className="flex-grow-1">
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <h6 className="mb-0 small text-wrap">{notification.title}</h6>
                        <Button 
                          variant="light-lite" 
                          size="sm" 
                          className="py-0 px-1 ms-auto"
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            dismissNotification(notification.id);
                          }}
                        >
                          <i className="bi bi-x"></i>
                        </Button>
                      </div>
                      <TruncatedText text={notification.body} notificationId={notification.id} />
                      <div className="mb-2 d-flex justify-content-between align-items-center">
                        <small className="text-muted">
                          {formatTimeAgo(notification.createdAt)}
                        </small>
                      </div>
                    </div>
                  </div>
                </ListGroup.Item>
                  );
                })()
              ))
            )}
          </ListGroup>
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
};

export default NotificationsDropdown; 